import { useEffect, useState } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { refreshAccessToken } from '@/api/tokenRefresh'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { usePublicLightTheme } from '@/hooks/usePublicLightTheme'
import { useAuthStore } from '@/store/authStore'

const REDIRECT_WHEN_AUTHED = new Set(['/', '/login', '/register'])

/**
 * Public auth/marketing routes. Restores the session via refresh cookie first,
 * then sends already-authenticated visitors away from landing/login/register.
 */
export function GuestGuard() {
  usePublicLightTheme()
  const location = useLocation()
  const accessToken = useAuthStore((state) => state.accessToken)
  const user = useAuthStore((state) => state.user)
  const [isChecking, setIsChecking] = useState(!accessToken || !user)

  useEffect(() => {
    if (accessToken && user) {
      setIsChecking(false)
      return
    }

    let isMounted = true
    refreshAccessToken()
      .catch(() => {
        /* stay as guest */
      })
      .finally(() => {
        if (isMounted) {
          setIsChecking(false)
        }
      })

    return () => {
      isMounted = false
    }
  }, [accessToken, user])

  if (isChecking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <LoadingSpinner />
      </div>
    )
  }

  if (accessToken && user && REDIRECT_WHEN_AUTHED.has(location.pathname)) {
    return <Navigate to="/dashboard" replace />
  }

  return <Outlet />
}
